import { Booking, CANCELLATION_FEE_PRODUCT_KEY, LegacyOrder, ProductInfo } from '@luggagehero/shared/interfaces';

import { SharedUtilDate } from './shared-util-date';

/** What the cancel dialog says above the reasons (AM-4788): a translation key + its `{{deadline}}`, if any. */
export interface CancellationPolicyNote {
  /** `CANCEL_UNTIL_MIDNIGHT_EXPLANATION` is the pre-AM-4788 note, rendered where no fee is priced. */
  key:
    | 'CANCEL_UNTIL_MIDNIGHT_EXPLANATION'
    | 'CANCEL_FREE_UNTIL_DEADLINE'
    | 'CANCEL_DEADLINE_PASSED'
    | 'CANCEL_FREE_WITHIN_GRACE'
    | 'CANCEL_GRACE_PASSED';
  params: { deadline?: string };
}

type DeadlineSource =
  | Pick<LegacyOrder, 'freeUntil' | 'freeUntilBasis' | 'timezone'>
  | Pick<Booking, 'freeUntil' | 'freeUntilBasis' | 'timezone'>;

/**
 * What a pre-booking surface promises (AM-4788): the exact instant when a quote carries a cut-off
 * deadline, "within 15 minutes of booking" when the grace period sets it, else the policy.
 */
export interface CancellationPromise {
  /**
   * `FREE_CANCELLATION` is the pre-AM-4788 copy, `quotePromise`'s answer where no fee is priced. No
   * caller renders it from here today: the one consumer, the "More info" window, uses its own
   * master bullet for that case.
   */
  key:
    | 'FREE_CANCELLATION'
    | 'FREE_CANCELLATION_UNTIL'
    | 'FREE_CANCELLATION_WITHIN_GRACE'
    | 'FREE_CANCELLATION_UNTIL_DROPOFF_MORNING';
  /**
   * `deadline` for the pinned instant; `time` for the POLICY hour on the no-quote branch (AM-4788
   * review). They are mutually exclusive by construction — a surface either knows the booking's own
   * deadline or states the rule — and each key carries exactly the one it needs.
   */
  params: { deadline?: string; time?: string };
}

/** One rendered line of the "More info" window: a translation key and whatever it interpolates. */
export interface CancellationInfoLine {
  key: string;
  params: Record<string, unknown>;
}

/**
 * What the confirm-booking "More info" window says about cancellation (AM-4788).
 *
 * Pure, and it lives here rather than in the component for the reason the rest of this file does:
 * `libs-legacy` has a lint target and no test project, so a decision left in the base component
 * cannot be tested at all. Here both branches are covered.
 *
 * Three cases:
 *   - **No priced fee** — cancelling late costs nothing, so there is nothing for a deadline to be
 *     the edge of. The bullet is master's `CANCEL_UNTIL_MIDNIGHT` ("Free cancellation, any time."),
 *     in either flag state. NOT `quotePromise`'s `FREE_CANCELLATION` ("Free cancellation"): that
 *     drops the "any time" qualification, on exactly the locations this round promises not to
 *     touch. The review panel caught the branch doing precisely that.
 *   - **Priced, flag off (the default)** — the RULE, then its cost: the policy with the server's
 *     cut-off hour, then what a late cancellation is charged. This window is the only cancellation
 *     statement a sighted traveler sees on the panel: the Summary's row is still rendered, but
 *     `visually-hidden`, so assistive technology keeps the deadline the mouse-only tooltip cannot
 *     give it.
 *   - **Priced, flag on** — this booking's own pinned instant instead of the rule. Strictly more
 *     specific, but it describes one booking rather than the policy. Kept switchable so the two
 *     wordings can be compared without a release; see `freeCancellationMoreInfoPinnedDeadline`.
 */
export function moreInfoCancellationKeys(
  promise: CancellationPromise,
  feeIsPriced: boolean,
  pinnedDeadlineEnabled: boolean,
  policyTime: string,
): CancellationInfoLine[] {
  if (!feeIsPriced) {
    //
    // Master's bullet, restored verbatim. `quotePromise` answers a different question — what a
    // pre-booking SURFACE promises — and its no-fee answer, `FREE_CANCELLATION`, is a shorter,
    // weaker sentence than the one this window showed before AM-4788. Reusing it here silently
    // changed copy on storage locations the whole round claims not to touch.
    //
    return [{ key: 'CANCEL_UNTIL_MIDNIGHT', params: {} }];
  }
  if (pinnedDeadlineEnabled) {
    return [{ key: promise.key, params: { ...promise.params } }];
  }
  return [
    { key: 'FREE_CANCELLATION_POLICY_SAME_DAY_EXPLANATION', params: { time: policyTime } },
    { key: 'CANCELLATION_FEE_INFO_BODY', params: {} },
  ];
}

/**
 * Whether a cancellation after the deadline would actually COST anything — the predicate the whole
 * dated-deadline copy hangs on (AM-4788, round 8).
 *
 * The price row is the feature flag. `requestCancelOrder` on the api charges exactly one line, the
 * `CANCELLATION_FEE` product priced in the booking's PINNED product list, and zeroes every other
 * line; if that product is absent or costs nothing, cancelling late is free. So a surface may only
 * state a dated deadline, an after-deadline note or a charge where that row exists and is priced.
 * Everywhere else the traveler sees exactly the copy they saw before this ticket, because nothing
 * about their booking has changed.
 *
 * Read per booking or quote, never per market: the pinned list is a snapshot taken when the order
 * was priced, so a row added or zeroed afterwards does not retroactively change what an existing
 * booking promises. That is also the rollback: zero the rows and the new copy stops rendering,
 * with no release.
 *
 * `cost > 0` rather than "the key exists": a row priced at zero IS the fee switched off, and at
 * least one context ships exactly that.
 */
export function hasPricedCancellationFee(products: readonly ProductInfo[] | null | undefined): boolean {
  return !!products?.some((p) => p?.key === CANCELLATION_FEE_PRODUCT_KEY && typeof p.cost === 'number' && p.cost > 0);
}

/**
 * The note under the date pickers on the confirm-booking panel.
 *
 * `CHOOSE_DATES_AND_CANCEL_EXPLANATION` says "You can cancel your booking at any time", which is
 * false wherever a cancellation fee is priced — and it sits directly above a Summary row promising
 * free cancellation only until a stated deadline, or only for fifteen minutes. Two statements about
 * cancelling, on one screen, disagreeing.
 *
 * Where a fee is priced this returns `CHOOSE_DATES_EXPLANATION`, which keeps the sentence the note is
 * actually for ("Choose your dates") and says something true about the stay rather than about
 * cancelling. The free-cancellation promise is deliberately NOT repeated here: the Summary row is its
 * single source, so there is one statement to keep correct instead of two.
 *
 * Both keys are already seeded, so this needs no migration.
 *
 * Do not read that as "the deploy guard would have caught it otherwise". It would not have:
 * `check-translation-keys.mjs` matches a quoted literal before `| translate` or inside `.instant(…)`,
 * and every key this round introduces is returned from a getter or carried as an object-literal
 * `key:` field — invisible to it. Verified with `--print-referenced`, with `PRICING` and
 * `BOOK_AHEAD_FOR_BEST_RATES` as the positive control that extraction itself still works.
 *
 * Gated on the quote's own pinned list, the same per-quote flag every other AM-4788 surface reads,
 * never a market-wide setting. With no priced fee the original note is returned unchanged, because
 * there it is true.
 */
export function chooseDatesNoteKey(products: readonly ProductInfo[] | null | undefined): string {
  return hasPricedCancellationFee(products) ? 'CHOOSE_DATES_EXPLANATION' : 'CHOOSE_DATES_AND_CANCEL_EXPLANATION';
}

/**
 * The free-cancellation deadline as the cancel flow presents it (AM-4788). Pure so it can be
 * unit-tested — the legacy base components that render it have no test project.
 */
export class SharedUtilCancellation {
  /**
   * The promise a pre-booking surface makes. Its one consumer today is the confirm-page "More
   * info" window, which uses the result only where a fee is priced and
   * `freeCancellationMoreInfoPinnedDeadline` is on; the wizard banner no longer reads it (it is
   * simply not rendered where a fee is priced).
   *
   * Where a fee is priced: the exact provisional deadline once the running quote carries one —
   * rendered in the quote's timezone, else the storage location's — or the policy itself while no
   * quote is in or none is renderable. The policy is never false (the pinned deadline can only
   * extend it), and none of the priced branches is a bare "free cancellation". Where no fee is
   * priced: exactly that bare `FREE_CANCELLATION`, master's label — see the guard below.
   */
  public static quotePromise(
    order: Pick<LegacyOrder, 'freeUntil' | 'freeUntilBasis' | 'timezone'> | null | undefined,
    fallbackTimezone: string | undefined,
    policyTime: string,
    locale?: string,
    feeIsPriced = false,
  ): CancellationPromise {
    //
    // No priced cancellation fee means cancelling late costs nothing, so there is nothing for a
    // deadline to be the edge of: the answer is the pre-AM-4788 promise, unchanged. The flag is read
    // per quote, not per market. Kept although the one caller discards this answer: with the
    // `feeIsPriced = false` default, a future caller that forgets the flag gets master's copy rather
    // than a dated promise.
    //
    if (!feeIsPriced) {
      return { key: 'FREE_CANCELLATION', params: {} };
    }
    //
    // Two cases get the undated grace promise rather than an instant.
    //
    // A `grace` basis, because the grace period is a safety net, not a time to watch — it is never
    // an instant and needs none.
    //
    // And a cut-off deadline that has ALREADY PASSED while the traveler sat on the page. A
    // pre-booking quote is not re-taken unless a criteria input changes, so it does not self-heal: a same-day
    // quote taken just before 09:00, or a tab left open and resumed, would otherwise read "Free
    // cancellation until 9:00am, Sep 27" at 09:40. The grace wording is not a consolation there, it
    // is the truth — the booking would pin `max(cutoff, now + 15 min)`, which at that moment IS the
    // grace floor. The two sibling renderers already refuse a passed deadline
    // (`cancellationPolicyNote` here, `isDeadlineAhead` in shared-order.service); this is the third
    // site, and it was the one that got missed.
    //
    // Note `deadlinePassed` is deliberately NOT `!deadlineAhead`: an UNPARSEABLE pin is neither
    // ahead nor passed, it is unknown, and an unknown pin must fall through to the policy line
    // rather than assert a grace window that may not apply. The existing "none is renderable" case
    // is what catches the difference.
    const freeUntilMs = order?.freeUntil ? new Date(order.freeUntil).getTime() : NaN;
    const deadlinePassed = !isNaN(freeUntilMs) && freeUntilMs <= Date.now();
    if (order?.freeUntil && (order.freeUntilBasis === 'grace' || deadlinePassed)) {
      return { key: 'FREE_CANCELLATION_WITHIN_GRACE', params: {} };
    }
    const deadline = order?.freeUntil
      ? SharedUtilDate.formatFreeCancellationDeadline(order.freeUntil, order.timezone ?? fallbackTimezone, locale)
      : '';
    return deadline
      ? { key: 'FREE_CANCELLATION_UNTIL', params: { deadline } }
      : // No quote to pin an instant: state the RULE, with the server's own hour rendered in the
        // viewer's 12-/24-hour convention. `policyTime` is never empty (the resolver falls back to
        // the api's default), because an unfilled token renders as literal braces to the traveler.
        { key: 'FREE_CANCELLATION_UNTIL_DROPOFF_MORNING', params: { time: policyTime } };
  }

  /**
   * The deadline this cancellation is judged against, rendered at the storage location. The cancel
   * quote carries the booking's pinned `freeUntil` (the very instant the server compared `now`
   * against), so it comes first; the booking itself is the fallback. `''` when neither has one — a
   * booking from before the pin existed — so the caller makes no dated promise.
   */
  public static freeCancellationDeadline(
    order: DeadlineSource | null | undefined,
    booking: DeadlineSource | null | undefined,
    locale?: string,
  ): string {
    const freeUntil = order?.freeUntil ?? booking?.freeUntil;
    const timezone = order?.timezone ?? booking?.timezone;
    return freeUntil ? SharedUtilDate.formatFreeCancellationDeadline(freeUntil, timezone, locale) : '';
  }

  /**
   * The policy line: free until the deadline, or that the deadline has passed — whichever the
   * server's cancel quote says (`total === 0` is free), and always with the instant.
   *
   * **With no order at all it returns the UNDATED line, not `null`.** That is deliberate and easy
   * to "fix" back into a bug, so it is worth the sentence: a legacy booking (`schemaVersion < 3`)
   * never gets a quote — the cancel action returns early and never fetches one — while the template
   * still renders this paragraph. Returning `null` there dropped the policy sentence master printed
   * unconditionally, leaving the dialog saying only that the booking can be modified. The undated
   * line is what master said and is never a dated promise, so it is also the right thing to show in
   * the instant before a modern booking's quote lands. `shared-util-cancellation.spec.ts` names this
   * case; if you are here to restore a `return null`, that test is the one you will have to delete.
   *
   * `null` is reserved for the cases where there is a quote but nothing honest to say with it: no
   * deadline to name (a bare "free cancellation" is exactly the promise AM-4788 removes), or a quote
   * that is free while the deadline has already passed — a cancellation the server forces free (an
   * incident, a fail-open). "Free until <a time that has gone>" would read as a mistake; the
   * zero-priced summary already says it is free.
   */
  public static cancellationPolicyNote(
    order: (DeadlineSource & Pick<LegacyOrder, 'total'>) | null | undefined,
    booking: DeadlineSource | null | undefined,
    locale?: string,
    feeIsPriced = false,
  ): CancellationPolicyNote | null {
    if (!order) {
      //
      // No order at all. For a modern booking that is the moment before the quote lands, and a
      // blank beats a promise we cannot keep. For a LEGACY booking (`schemaVersion < 3`) the quote
      // never lands — `CancelBookingActionBaseComponent` returns early and never fetches one — yet
      // the dialog still renders this paragraph, so `null` would silently drop the policy sentence
      // master printed unconditionally. There is nothing booking-specific left to say in that
      // state, and the undated line is exactly what master said, so say it.
      //
      // The two cases are told apart by the booking: a legacy booking is still a booking, a
      // pre-quote modern one has one too — so the discriminator is the order's absence plus
      // whether there is anything to wait for, which only the caller knows. Returning the undated
      // line in both is the safe answer: for the pre-quote instant it is what the dialog showed a
      // moment earlier anyway, and it is never a dated promise.
      //
      return { key: 'CANCEL_UNTIL_MIDNIGHT_EXPLANATION', params: {} };
    }
    //
    // Nothing is charged for a late cancellation on this booking, so the dialog says what it always
    // said. Note this branch returns a NOTE rather than null: master rendered this line
    // unconditionally, and the bar for a no-fee booking is the dialog it has today, byte for byte.
    //
    if (!feeIsPriced) {
      return { key: 'CANCEL_UNTIL_MIDNIGHT_EXPLANATION', params: {} };
    }
    const deadline = SharedUtilCancellation.freeCancellationDeadline(order, booking, locale);
    if (!deadline) {
      return null;
    }
    const isFree = order.total === 0;
    const freeUntil = new Date(order.freeUntil ?? booking?.freeUntil ?? NaN);
    if (isFree && !isNaN(freeUntil.getTime()) && freeUntil.getTime() <= Date.now()) {
      return null;
    }
    // A grace-based deadline is told as the 15-minute window, never as its instant.
    const basis = order.freeUntilBasis ?? booking?.freeUntilBasis;
    if (basis === 'grace') {
      return { key: isFree ? 'CANCEL_FREE_WITHIN_GRACE' : 'CANCEL_GRACE_PASSED', params: {} };
    }
    return { key: isFree ? 'CANCEL_FREE_UNTIL_DEADLINE' : 'CANCEL_DEADLINE_PASSED', params: { deadline } };
  }
}
